import {
  Banknote,
  Banknote as BanknoteIcon,
  BarChart3,
  Briefcase,
  Calendar,
  CheckCircle2,
  CircleDollarSign,
  CreditCard,
  FileText,
  FileWarning,
  Mail,
  MessageSquare,
  Phone,
  TrendingDown,
  TrendingUp,
  User,
} from 'lucide-react';
import type { CurrencyMode } from '@/components/shared/currency-toggle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { DEFAULT_AGEING_BANDS, useAgeingBands } from '@/hooks/useAgeingBands';
import { useCustomerRecentActivities } from '@/hooks/useCustomerRecentActivitiesGraphQL';
import { useCustomerMetricTotals } from '@/hooks/useTransactionsGraphQL';
import { ACTIVITY_TYPE_TO_TYPE } from '@/lib/activity-type-maps';
import { formatDate as formatDateShared } from '@/lib/utils';
import type { CustomerAgeingData } from '@/types/ageing-analysis';

interface CustomerOverviewTabProps {
  customer: CustomerAgeingData;
  customerId?: string;
  formatCurrency: (amount: number, currency?: string) => string;
  currencyMode?: CurrencyMode;
}

export function CustomerOverviewTab({
  customer,
  customerId,
  formatCurrency,
  currencyMode = 'customer',
}: Readonly<CustomerOverviewTabProps>) {
  const isBaseCurrencyCustomer = customer.currency === customer.homeCurrencyCode;
  const useBaseBands = currencyMode === 'base' || isBaseCurrencyCustomer;
  const displayCurrency = useBaseBands ? customer.homeCurrencyCode : customer.currency;
  const formatAmount = (amount: number) => formatCurrency(amount, displayCurrency);

  const customerIdNum = customerId ? parseInt(customerId, 10) : undefined;
  const { activities, isLoading: activitiesLoading } = useCustomerRecentActivities(customerIdNum);
  const { data: metricTotals, isLoading: metricsLoading } = useCustomerMetricTotals(
    customerId,
    customer.currency || undefined,
  );

  const effectiveOutstanding = useBaseBands ? customer.baseTotalOutstanding : customer.totalOutstanding;
  const effectiveCreditLimit = useBaseBands
    ? customer.creditLimit
    : (customer.currencyCreditLimit ?? customer.creditLimit);
  const availableCredit = effectiveCreditLimit - effectiveOutstanding;
  const creditUtilisation =
    effectiveCreditLimit > 0 ? Math.min(100, Math.round((effectiveOutstanding / effectiveCreditLimit) * 100)) : 0;

  const { data: ageingBands } = useAgeingBands();
  const bands = ageingBands ?? DEFAULT_AGEING_BANDS;

  const bandData = useBaseBands ? customer.baseBands : customer.bands;
  const ageingBuckets = bands.map((band) => ({
    label: band.bandLabel,
    value: bandData[band.sequence] ?? 0,
    colour: band.color || '#6b7280',
  }));

  const totalAgeing = ageingBuckets.reduce((sum, b) => sum + b.value, 0);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="h-3.5 w-3.5" />;
      case 'email':
        return <Mail className="h-3.5 w-3.5" />;
      case 'payment':
        return <Banknote className="h-3.5 w-3.5" />;
      case 'promise':
        return <CheckCircle2 className="h-3.5 w-3.5" />;
      case 'note':
        return <MessageSquare className="h-3.5 w-3.5" />;
      case 'dispute':
        return <FileWarning className="h-3.5 w-3.5" />;
      case 'letter':
        return <FileText className="h-3.5 w-3.5" />;
      case 'meeting':
        return <Calendar className="h-3.5 w-3.5" />;
      default:
        return <MessageSquare className="h-3.5 w-3.5" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'call':
        return 'bg-blue-100 text-blue-700';
      case 'email':
        return 'bg-purple-100 text-purple-700';
      case 'payment':
        return 'bg-green-100 text-green-700';
      case 'promise':
        return 'bg-cyan-100 text-cyan-700';
      case 'note':
        return 'bg-gray-100 text-gray-700';
      case 'dispute':
        return 'bg-red-100 text-red-700';
      case 'letter':
        return 'bg-orange-100 text-orange-700';
      case 'meeting':
        return 'bg-indigo-100 text-indigo-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateStr?: string) => (dateStr ? formatDateShared(dateStr) : '—');

  return (
    <div className="space-y-4">
      {/* Row 1 — Quick Stat Cards */}
      <div className="grid grid-cols-4 gap-3">
        {/* Outstanding */}
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Outstanding</p>
                <p className="text-xl font-bold mt-1 leading-none">{formatAmount(effectiveOutstanding)}</p>
              </div>
              <div className="p-2.5 rounded-full bg-red-100 flex-shrink-0">
                <CircleDollarSign className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credit Limit */}
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Credit Limit</p>
                <p className="text-xl font-bold mt-1 leading-none">{formatAmount(effectiveCreditLimit)}</p>
              </div>
              <div className="p-2.5 rounded-full bg-blue-100 flex-shrink-0">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Available Credit */}
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Available Credit</p>
                <p className="text-xl font-bold mt-1 leading-none">{formatAmount(availableCredit)}</p>
              </div>
              <div className="p-2.5 rounded-full bg-green-100 flex-shrink-0">
                <BarChart3 className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Payment */}
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Last Payment</p>
                <p className="text-sm font-bold mt-1 leading-none">{formatDate(customer.lastPayment)}</p>
              </div>
              <div className="p-2.5 rounded-full bg-purple-100 flex-shrink-0">
                <BanknoteIcon className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2 — Account Details + Ageing Breakdown */}
      <div className="grid grid-cols-2 gap-3">
        {/* Account Details */}
        <Card>
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-sm font-semibold">Account Details</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <p className="text-xs text-muted-foreground">Account #</p>
                <p className="text-sm font-medium mt-0.5">{customer.accountNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Currency</p>
                <p className="text-sm font-medium mt-0.5">{customer.currency}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Payment Terms</p>
                <p className="text-sm font-medium mt-0.5">{customer.terms}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Risk Level</p>
                <p className="text-sm font-medium mt-0.5 capitalize">{customer.riskLevel}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Collection Strategy</p>
                <p className="text-sm font-medium mt-0.5">{customer.collectionStrategy || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Days Overdue</p>
                <p className="text-sm font-medium mt-0.5">{customer.averageDaysOverdue} days</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Email
                </p>
                <p className="text-sm font-medium mt-0.5 truncate">{customer.contact || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" /> Phone
                </p>
                <p className="text-sm font-medium mt-0.5">{customer.phone || '—'}</p>
              </div>
              {customer.creditControllerName && (
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" /> Credit Controller
                  </p>
                  <p className="text-sm font-medium mt-0.5">{customer.creditControllerName}</p>
                </div>
              )}
              {customer.accountManagerName && (
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Briefcase className="h-3 w-3" /> Account Manager
                  </p>
                  <p className="text-sm font-medium mt-0.5">{customer.accountManagerName}</p>
                </div>
              )}
              {(customer.userSortKey1 || customer.userSortKey2 || customer.userSortKey3 || customer.userSortKey4) && (
                <>
                  {customer.userSortKey1 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Sort Key 1</p>
                      <p className="text-sm font-medium mt-0.5">{customer.userSortKey1}</p>
                    </div>
                  )}
                  {customer.userSortKey2 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Sort Key 2</p>
                      <p className="text-sm font-medium mt-0.5">{customer.userSortKey2}</p>
                    </div>
                  )}
                  {customer.userSortKey3 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Sort Key 3</p>
                      <p className="text-sm font-medium mt-0.5">{customer.userSortKey3}</p>
                    </div>
                  )}
                  {customer.userSortKey4 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Sort Key 4</p>
                      <p className="text-sm font-medium mt-0.5">{customer.userSortKey4}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ageing Breakdown */}
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold">Ageing Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {/* Stacked bar */}
            <div className="flex h-3 rounded-full overflow-hidden mb-3 gap-px">
              {ageingBuckets.map((bucket) => {
                const pct = totalAgeing > 0 ? (bucket.value / totalAgeing) * 100 : 0;
                return pct > 0 ? (
                  <div
                    key={bucket.label}
                    className="transition-all"
                    style={{ width: `${pct}%`, backgroundColor: bucket.colour }}
                    title={`${bucket.label}: ${formatAmount(bucket.value)} (${Math.round(pct)}%)`}
                  />
                ) : null;
              })}
            </div>
            {/* Bucket rows */}
            <div className="space-y-2.5">
              {ageingBuckets.map((bucket) => {
                const pct = totalAgeing > 0 ? Math.round((bucket.value / totalAgeing) * 100) : 0;
                return (
                  <div key={bucket.label} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: bucket.colour }} />
                    <p className="text-xs text-muted-foreground w-28 flex-shrink-0">{bucket.label}</p>
                    <div className="flex-1 min-w-0">
                      <Progress value={pct} className="h-1.5" />
                    </div>
                    <p className="text-xs font-medium w-24 text-right flex-shrink-0">{formatAmount(bucket.value)}</p>
                    <p className="text-xs text-muted-foreground w-8 text-right flex-shrink-0">{pct}%</p>
                  </div>
                );
              })}
            </div>
            {/* Total */}
            <div className="mt-3 pt-3 border-t flex justify-between items-center">
              <p className="text-xs font-semibold text-muted-foreground">Total Outstanding</p>
              <p className="text-sm font-bold">{formatAmount(totalAgeing)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3 — Credit & Payment Summary + Recent Activity (compact) */}
      <div className="grid grid-cols-2 gap-3">
        {/* Credit & Payment Summary */}
        <Card>
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-sm font-semibold">Credit & Payment Summary</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {metricsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : (
              <div className="space-y-2">
                {/* Credit Utilisation */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs text-muted-foreground">Credit Utilisation</p>
                    <p className="text-xs font-semibold">{creditUtilisation}%</p>
                  </div>
                  <Progress value={creditUtilisation} className="h-2" />
                </div>
                {/* Grid of metrics */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
                  <div>
                    <p className="text-xs text-muted-foreground">Avg Payment Days</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <p className="text-sm font-semibold">{customer.averageDaysOverdue} days</p>
                      {customer.averageDaysOverdue <= 30 ? (
                        <TrendingUp className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <TrendingDown className="h-3.5 w-3.5 text-red-600" />
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Overdue Invoices</p>
                    <p className="text-sm font-semibold mt-0.5">{metricTotals.overdueCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last Payment</p>
                    <p className="text-sm font-semibold mt-0.5">{formatDate(customer.lastPayment)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Available Credit</p>
                    <p className="text-sm font-semibold mt-0.5">{formatAmount(availableCredit)}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity — compact, 3 items */}
        <Card>
          <CardHeader className="pb-1 pt-3 px-3">
            <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {activitiesLoading && (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}
            {!activitiesLoading && activities.length > 0 && (
              <div className="space-y-2">
                {activities.slice(0, 3).map((activity, index) => {
                  const uiType = ACTIVITY_TYPE_TO_TYPE[activity.activityType] ?? activity.activityType;
                  return (
                    <div key={activity.id} className="flex gap-2.5">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`p-1.5 rounded-full ${getActivityColor(uiType)}`}>
                          {getActivityIcon(uiType)}
                        </div>
                        {index < 2 && <div className="w-px flex-1 bg-border mt-1.5 min-h-[16px]" />}
                      </div>
                      <div className="flex-1 min-w-0 pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium leading-none">{activity.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{activity.description}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-muted-foreground">{formatDateShared(activity.createdAt)}</p>
                            {activity.actorName && (
                              <p className="text-xs text-muted-foreground mt-0.5">{activity.actorName}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!activitiesLoading && activities.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-20" />
                <p className="text-xs">No recent activity recorded</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
